import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Space, Skeleton, Tooltip, Divider } from "antd";
import StyledModal from "../../../../components/CustomModal";
import { FooterButtons, StyledButton } from "../../../../components/styles";
import StyledNumberInput from "../../../../components/StyledNumberInput";
import { Form, message } from "antd";
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
    DimensionsGroup,
    DimensionsErrorMessage,
    DimensionsTableCellEditable,
    DimensionsTableCell,
    DimensionsTableContainer,
    DimensionsTableData,
    DimensionsTableFooter,
    DimensionsTableHeader,
    DimensionsHeaderWithEdit,
    DimensionsMeasurementUnitWrapper,
    DimensionsTableRow,
    DimensionsShipFromContainer,
    DimensionsShipFromHeader,
    DimensionsShipFromTable,
    DimensionsShipFromTableHeader,
    ShipFromRow,
} from "./styles";
import { ShippingOption } from "../../../../types/ShipTypes";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { getPackage, updatePackage } from "../../../../redux/features/report/reportAsyncThunk";
import { Package } from "../../../../types/ShipTypes";
import { selectUserRole } from "../../../../redux/features/auth/selectors";
import { checkHasPermission } from "../../../../utils/helpers/checkHasPermission";
import { permissions } from "../../../../utils/constants/permissions";
import { convertUtcToLocal } from "../../../../utils/helpers/convertUtcToLocal";
import { enableCutOffDate } from "../../../../utils/constants";
import { MeasurementType, PaymentStatus } from "../../../../types";
import { setKeyValue } from "../../../../redux/features/report/reportSlice";
import { Dimensions, UnitType } from "../../../../types/FoundItemTypes";
import { convertToApiFormat } from "../../../../utils/helpers/unitConversion";
import { convertDimensionsUnits, getErrorMessage, getUnitLabels } from "../../utils/formula";
import { fetchShippingBoxType } from "../../../../redux/features/shippingBoxType/shippingBoxTypeAsyncThunk";
import { fetchCouriers } from "../../../../redux/features/courier/courierAsyncThunk";
import { CourierStatus } from "../../../../types/CourierTypes";
interface ShipFromData {
    selected: boolean;
    isSelected: boolean;
    cutOffDate: string;
    contactName: string;
    address: string;
    email: string;
    phone: string;
    price?: string;
    courier?: string;
    shippingOption?: string;
}


interface ConfirmationModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

interface LimitValue {
    main: number;
    sub: number;
}

interface Limits {
    length: LimitValue;
    width: LimitValue;
    height: LimitValue;
    weight: LimitValue;
}

const IMPERIAL_LIMITS: Limits = {
    length: { main: 99, sub: 11.99 },
    width: { main: 99, sub: 11.99 },
    height: { main: 99, sub: 11.99 },
    weight: { main: 15, sub: 15.99 }
};

const METRIC_LIMITS: Limits = {
    length: { main: 99, sub: 99.99 },
    width: { main: 99, sub: 99.99 },
    height: { main: 99, sub: 99.99 },
    weight: { main: 999, sub: 999.99 }
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ isOpen, onCancel, onConfirm }) => {
    return (
        <StyledModal
            draggable={true}
            title="confirmation"
            open={isOpen}
            onCancel={onCancel}
            width={600}
            centered
            footer={
                <FooterButtons>
                    <StyledButton onClick={onCancel}>No</StyledButton>
                    <StyledButton type="primary" onClick={onConfirm}>
                        Yes, I understand
                    </StyledButton>
                </FooterButtons>
            }
        >
            <div style={{ color: "#ff4d4f", fontSize: "16px", marginBottom: "24px", paddingTop: "24px" }}>The Cut-off date is less than 60 hours away. Are you sure you want to proceed with adding pricing?</div>
        </StyledModal>
    );
};

const DimensionsTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const { report, package: packageData, packageLoading, shippingOptions } = useAppSelector((state) => state.report);
    const [dimensions, setDimensions] = useState<Dimensions>();
    const [shipFromData, setShipFromData] = useState<ShippingOption[]>([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<number | null>(null);
    const [selectedShipFromRow, setSelectedShipFromRow] = useState<ShippingOption | null>(null);
    const [unit, setUnit] = useState<UnitType>(1);
    const [form] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const userRole = useAppSelector(selectUserRole);
    const isFinalMatchPermission = useMemo(() => checkHasPermission(userRole, permissions.IsFinalMatchStatusEnabled), [userRole]);

    useEffect(() => {
        if (report?.id && report?.foundItem?.id) {
            dispatch(getPackage({
                lostId: report.id,
                foundId: report.foundItem.id
            }));
            dispatch(fetchCouriers({ status: CourierStatus.Active }));
            dispatch(fetchShippingBoxType({})).unwrap();
        }
    }, [report?.id, report?.foundItem?.id]);

    useEffect(() => {
        if (packageData) {
            const newDimensions: Dimensions = {
                length: {
                    main: packageData.lengthPrimaryValue ?? 0,
                    sub: packageData.lengthSecondaryValue ?? 0
                },
                width: {
                    main: packageData.widthPrimaryValue ?? 0,
                    sub: packageData.widthSecondaryValue ?? 0
                },
                height: {
                    main: packageData.heightPrimaryValue ?? 0,
                    sub: packageData.heightSecondaryValue ?? 0
                },
                weight: {
                    main: packageData.weightPrimaryValue ?? 0,
                    sub: packageData.weightSecondaryValue ?? 0
                }
            };
            form.setFieldsValue(newDimensions);
            setUnit(packageData.measurementType === MeasurementType.Metric ? 2 : 1);
            setDimensions(newDimensions);
        }
    }, [packageData]);

    // Update shipping data when options change
    useEffect(() => {
        if (shippingOptions && shippingOptions.length > 0) {
            setShipFromData(shippingOptions);
        }
    }, [shippingOptions]);

    const isPaymentSucceeded = useMemo(() => report?.payment?.some((payment) => payment.status === PaymentStatus.Succeeded), [report]);



    const handleValueChange = (value: string | number | null, type: "length" | "width" | "height" | "weight", field: "main" | "sub") => {
        const currentValues = {
            main: form.getFieldValue([type, "main"]) || 0,
            sub: form.getFieldValue([type, "sub"]) || 0,
        };

        let newValues = { ...currentValues };

        if (field === "main") {
            const numValue = validateNumber(value);
            newValues.main = numValue !== null ? numValue : 0;
            form.setFieldsValue({
                [type]: newValues,
            });
        } else if (field === "sub") {
            const numValue = validateNumber(value);
            if (numValue !== null) {
                // No automatic rollover conversion - allow any value
                newValues.sub = numValue;
                form.setFieldsValue({
                    [type]: newValues,
                });
            }
        }
    };

    const handleSubValueBlur = (type: "length" | "width" | "height" | "weight") => {
        // No conversion logic needed anymore
        // This function is kept for consistency with Step3.tsx
    };

    const renderDimensionInput = (type: "length" | "width" | "height" | "weight") => {
        const [mainUnit, subUnit] = getUnitLabels(type, unit);
        if (!unit) return null;

        const getMainMaxValue = () => {
            // For weight: lb or kg (both 2 digits)
            if (type === "weight") return 99.99;
            // For dimensions: empty string means it's the sub-unit (cm/in)
            return 0;
        };

        const getSubMaxValue = () => {
            if (type === "weight") return 0; // No sub unit for weight
            return unit === 1 ? 99.99 : 999.99; // in: 2 digits (0-99.99), cm: 3 digits (0-999.99)
        };

        const validateMainValue = (value: number | null): string | void => {
            if (value === null || value === undefined) return;

            if (value < 0) return "Value cannot be negative";
            if (value <= 0) return getErrorMessage(type);

            // Allow decimal values with up to 2 decimal places
            const decimalPlaces = value.toString().split('.')[1]?.length || 0;
            if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

            const max = getMainMaxValue();
            if (max > 0 && value > max) return `Value cannot exceed ${max}`;
        };

        const validateSubValue = (value: number | null): string | void => {
            if (value === null || value === undefined) return;

            if (value < 0) return "Value cannot be negative";
            if (value <= 0) return getErrorMessage(type);

            // Allow decimal values with up to 2 decimal places
            const decimalPlaces = value.toString().split('.')[1]?.length || 0;
            if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

            const max = getSubMaxValue();
            if (value > max) return `Value cannot exceed ${max}`;
        };

        return (
            <DimensionsGroup>
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => {
                        return prevValues[type] !== currentValues[type];
                    }}
                >
                    {({ getFieldValue, getFieldError }) => {
                        const mainErrors = mainUnit ? getFieldError([type, "main"]) : [];
                        const subErrors = subUnit ? getFieldError([type, "sub"]) : [];
                        const hasErrors = mainErrors.length > 0 || subErrors.length > 0;

                        return (
                            <>
                                <div className="edit-row">
                                    <Form.Item
                                        name={[type, "main"]}
                                        initialValue={0}
                                        rules={mainUnit ? [
                                            {
                                                validator: async (_, value) => {
                                                    const mainValue = validateNumber(value) ?? 0;
                                                    const subValue = validateNumber(getFieldValue([type, "sub"])) ?? 0;

                                                    if (mainValue === 0 && subValue === 0) {
                                                        throw new Error(`Please enter ${type}`);
                                                    }

                                                    const error = validateMainValue(mainValue);
                                                    if (error) throw new Error(error);
                                                },
                                            },
                                        ] : []}
                                        validateTrigger={["onChange", "onBlur"]}
                                        hidden={!mainUnit}
                                    >
                                        <StyledNumberInput
                                            size="middle"
                                            min={0}
                                            max={getMainMaxValue()}
                                            placeholder="00.00"
                                            unit={mainUnit}
                                            status={hasErrors ? "error" : ""}
                                            onChange={(value) => handleValueChange(value, type, "main")}
                                            allowDecimals={true}
                                            onFocus={(e) => {
                                                const value = getFieldValue([type, "main"]);
                                                if (value < 0.1) {
                                                    form.setFieldsValue({
                                                        [type]: { main: "", sub: "" },
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        initialValue={0}
                                        name={[type, "sub"]}
                                        rules={subUnit ? [
                                            {
                                                validator: async (_, value) => {
                                                    const subValue = validateNumber(value) ?? 0;
                                                    const error = validateSubValue(subValue);
                                                    if (error) throw new Error(error);
                                                },
                                            },
                                        ] : []}
                                        validateTrigger={["onChange", "onBlur"]}
                                        hidden={!subUnit}
                                    >
                                        <StyledNumberInput
                                            size="middle"
                                            min={0}
                                            max={getSubMaxValue()}
                                            placeholder={unit === 1 ? "00.00" : "000.00"}
                                            unit={subUnit}
                                            status={hasErrors ? "error" : ""}
                                            onChange={(value) => handleValueChange(value, type, "sub")}
                                            onBlur={() => handleSubValueBlur(type)}
                                            allowDecimals={true}
                                            onFocus={(e) => {
                                                const value = getFieldValue([type, "sub"]);
                                                if (value < 0.1) {
                                                    form.setFieldsValue({
                                                        [type]: { main: "", sub: "" },
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                                {/* {hasErrors && <DimensionsErrorMessage>{mainErrors[0] || subErrors[0]}</DimensionsErrorMessage>} */}
                            </>
                        );
                    }}
                </Form.Item>
            </DimensionsGroup>
        );
    };

    const validateNumber = (value: string | number | null): number | null => {
        if (value === null || value === "") return null;
        const num = typeof value === "string" ? parseFloat(value) : value;
        return isNaN(num) ? null : num;
    };

    const handleFormSubmit = async () => {
        try {
            const values = await form.validateFields();
            // Use values directly without automatic conversions
            const normalizedValues = {
                length: values.length,
                width: values.width,
                height: values.height,
                weight: values.weight,
            };

            // No maximum validation checks - allow any values
            // This is consistent with our changes to remove automatic rollover conversions

            if (!report?.id || !report?.foundItem?.id || !packageData?.id) {
                message.error("Missing report or found item ID");
                return;
            }
            // For imperial: inches only (no feet), for metric: cm only (no meters)
            const apiFormatData = convertToApiFormat(values, unit);

            // Set the update data based on how the UI displays and collects values
            const updateData = {
                ...apiFormatData,
                description: packageData?.description || "",
                lost: report.id,
                found: report.foundItem.id
            };
            await dispatch(updatePackage({
                id: packageData.id,
                data: updateData
            })).unwrap();

            setDimensions(normalizedValues);
            setIsEditing(false);
        } catch (error) {
            message.error("Please check all required fields");
        } finally {
        }
    };

    const handleUnitChange = async (newUnit: UnitType) => {
        const formValues = await form.getFieldsValue();
        const values = isEditing ? formValues : dimensions;
        if (!values) return;
        if (!values || unit === newUnit) return;
        const convertedValues = convertDimensionsUnits(values, unit, newUnit);
        setUnit(newUnit);
        setDimensions(convertedValues);
        form.setFieldsValue(convertedValues);
    };

    const handleCheckboxClick = (index: number) => {
        // Toggle isSelected flag for the shipping option
        const updatedData = shipFromData.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    isSelected: !item.isSelected
                };
            }
            return item;
        });
        setShipFromData(updatedData);
        dispatch(setKeyValue({ key: "shippingOptions", value: updatedData }));
    };

    const handleEditClick = (index: number, isClone: boolean) => {
        const cutOffDate = new Date(convertUtcToLocal(shipFromData[index].cutOffDate));
        const now = new Date();
        const hoursDiff = (now.getTime() - cutOffDate.getTime()) / (1000 * 60 * 60);

        setSelectedShipFromRow({ ...shipFromData[index], measurementType: unit, length: dimensions?.length?.sub, width: dimensions?.width?.sub, height: dimensions?.height?.sub, weight: dimensions?.weight?.sub || dimensions?.weight?.main, isClone });

        if (hoursDiff >= 0 && hoursDiff <= 60 && enableCutOffDate) {
            setSelectedRow(index);
            setConfirmModalOpen(true);
        } else {
            openEditModal(index);
        }
    };

    const openEditModal = (index: number) => {
        setSelectedRow(index);
        setEditModalOpen(true);
        setConfirmModalOpen(false);
    };

    const handleSave = (values: any) => {
        setEditModalOpen(false);
    };

    const formatDimension = (main: number, sub: number, isWeight: boolean) => {
        if (unit === 1) {
            const mainUnit = isWeight ? "lb" : "";
            const subUnit = isWeight ? "" : "in";
            return `${mainUnit ? main : ""} ${mainUnit} ${subUnit ? sub : ""} ${subUnit}`;
        } else {
            const mainUnit = isWeight ? "kg" : "";
            const subUnit = isWeight ? "" : "cm";
            return `${mainUnit ? main : ""} ${mainUnit} ${subUnit ? sub : ""} ${subUnit}`;
        }
    };

    const renderDimensionCell = (type: keyof Dimensions) => {
        if (packageLoading) {
            return (
                <DimensionsTableCell>
                    <span className="label">{type.charAt(0).toUpperCase() + type.slice(1)}:</span>
                    <Skeleton.Input active size="small" style={{ width: 120 }} />
                </DimensionsTableCell>
            );
        }

        return (
            <DimensionsTableCell>
                <span className="label">{type.charAt(0).toUpperCase() + type.slice(1)}:</span>
                <span className="value">
                    {formatDimension(
                        dimensions?.[type]?.main ?? 0,
                        dimensions?.[type]?.sub ?? 0,
                        type === "weight"
                    )}
                </span>
            </DimensionsTableCell>
        );
    };

    const paidShippingFrom = useMemo(() => {
        return report?.payment?.find((item) => item.status === PaymentStatus.Succeeded)?.shippingOption;
    }, [report?.payment]);

    return (
        <>
            <DimensionsTableContainer>
                <DimensionsTableHeader>
                    <DimensionsHeaderWithEdit>
                        <h2>Dimensions</h2>
                        {!isPaymentSucceeded && <EditOutlined className="edit-icon" onClick={() => setIsEditing(!isEditing)} />}
                    </DimensionsHeaderWithEdit>
                    <DimensionsMeasurementUnitWrapper>
                        <Space size={24}>
                            <Checkbox checked={unit === 1} onChange={(e) => handleUnitChange(e.target.checked ? 1 : 2)}>
                                inches/ lb
                            </Checkbox>
                            <Checkbox checked={unit === 2} onChange={(e) => handleUnitChange(e.target.checked ? 2 : 1)}>
                                cm/ kg
                            </Checkbox>
                            <Tooltip
                                title={
                                    <>
                                        Note: Select <b>Imperial System (inches/lb)</b> or <b>Metric System (cm/kg)</b>, then enter values in separate fields.
                                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                            <li>Inches (in): 0 to 99.99</li>
                                            <li>Pounds (lb): 0 to 99.99</li>
                                            <li>Kilograms (kg): 0 to 99.99</li>
                                            <li>Centimeters (cm): 0 to 999.99</li>
                                        </ul>
                                        All values can have up to 2 decimal places.
                                    </>
                                }
                                placement="right"
                                overlayStyle={{ maxWidth: 400 }}
                            >
                                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 8 }} />
                            </Tooltip>
                        </Space>
                    </DimensionsMeasurementUnitWrapper>
                </DimensionsTableHeader>
                <Form form={form} onFinish={handleFormSubmit}>
                    <DimensionsTableRow>
                        {isEditing ? (
                            <>
                                <DimensionsTableCellEditable>
                                    <span className="label">Length:</span>
                                    {renderDimensionInput("length")}
                                </DimensionsTableCellEditable>
                                <DimensionsTableCellEditable>
                                    <span className="label">Width:</span>
                                    {renderDimensionInput("width")}
                                </DimensionsTableCellEditable>
                                <DimensionsTableCellEditable>
                                    <span className="label">Height:</span>
                                    {renderDimensionInput("height")}
                                </DimensionsTableCellEditable>
                                <DimensionsTableCellEditable>
                                    <span className="label">Weight:</span>
                                    {renderDimensionInput("weight")}
                                </DimensionsTableCellEditable>
                            </>
                        ) : (
                            <>
                                {renderDimensionCell("length")}
                                {renderDimensionCell("width")}
                                {renderDimensionCell("height")}
                                {renderDimensionCell("weight")}
                            </>
                        )}
                    </DimensionsTableRow>
                    {isEditing && (
                        <DimensionsTableFooter>
                            <StyledButton onClick={() => setIsEditing(false)} style={{ marginRight: '8px' }}>
                                Cancel
                            </StyledButton>
                            <StyledButton type="primary" onClick={handleFormSubmit} loading={packageLoading} disabled={packageLoading}>
                                Save
                            </StyledButton>
                        </DimensionsTableFooter>
                    )}
                </Form>
            </DimensionsTableContainer>

            <DimensionsShipFromContainer>
                <DimensionsShipFromHeader>
                    <h2>Ship From</h2>
                </DimensionsShipFromHeader>
                <DimensionsShipFromTable>
                    <table>
                        <thead>
                            <tr>
                                {isFinalMatchPermission && <DimensionsShipFromTableHeader className="sticky">Include</DimensionsShipFromTableHeader>}
                                {isFinalMatchPermission && <DimensionsShipFromTableHeader>Action</DimensionsShipFromTableHeader>}
                                {enableCutOffDate && <DimensionsShipFromTableHeader>Cut-off Date</DimensionsShipFromTableHeader>}
                                <DimensionsShipFromTableHeader>Contact Name</DimensionsShipFromTableHeader>
                                <DimensionsShipFromTableHeader>Ship from Address</DimensionsShipFromTableHeader>
                                <DimensionsShipFromTableHeader>Email</DimensionsShipFromTableHeader>
                                <DimensionsShipFromTableHeader>Phone</DimensionsShipFromTableHeader>
                                <DimensionsShipFromTableHeader>Price ($)</DimensionsShipFromTableHeader>
                                <DimensionsShipFromTableHeader>Courier / Shipping Option</DimensionsShipFromTableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {shipFromData.map((row, index) => (
                                <ShipFromRow key={index} isHighlighted={!!paidShippingFrom && paidShippingFrom.id === row.id} dimmed={!!paidShippingFrom && paidShippingFrom.id !== row.id}>
                                    {isFinalMatchPermission && <DimensionsTableData className="sticky">
                                        <Checkbox
                                            checked={row.isSelected && row?.totalAmount > 0 && Boolean(row?.courier?.id)}
                                            onChange={() => handleCheckboxClick(index)}
                                            disabled={!row?.totalAmount || !row?.courier?.id}
                                        />
                                    </DimensionsTableData>}
                                    {isFinalMatchPermission && <DimensionsTableData>
                                        <EditOutlined onClick={() => handleEditClick(index, false)} />
                                        {shipFromData?.length < 3 && <><Divider type="vertical" />
                                            <PlusOutlined onClick={() => handleEditClick(index, true)} /></>}
                                    </DimensionsTableData>}
                                    {enableCutOffDate && <DimensionsTableData>{row?.cutOffDate ? convertUtcToLocal(row.cutOffDate, "MM/DD/YYYY") : "-"}</DimensionsTableData>}
                                    <DimensionsTableData>{(row?.port ?? "") + " " + (row?.contactName ?? "-")}</DimensionsTableData>
                                    <DimensionsTableData>{`${row?.address1 ?? ""} ${row?.address2 ?? ""}`}</DimensionsTableData>
                                    <DimensionsTableData>{row?.email ?? "-"}</DimensionsTableData>
                                    <DimensionsTableData>{row?.phone ? `+${row?.phone}` : "-"}</DimensionsTableData>
                                    <DimensionsTableData isActive={!!row?.totalAmount}>{row?.totalAmount?.toFixed(2) ?? "-"}</DimensionsTableData>
                                    <DimensionsTableData isActive={row?.courier?.name !== "Select Comment"}>{row?.courier?.name ?? "-"} {row?.shippingOption ? `/ ${row?.shippingOption}` : ""}</DimensionsTableData>
                                </ShipFromRow>
                            ))}
                        </tbody>
                    </table>
                </DimensionsShipFromTable>
            </DimensionsShipFromContainer>

            {confirmModalOpen && <StyledModal
                draggable={true}
                title="Confirmation"
                open={confirmModalOpen}
                onCancel={() => setConfirmModalOpen(false)}
                width={600}
                centered
                footer={
                    <FooterButtons>
                        <StyledButton onClick={() => setConfirmModalOpen(false)}>No</StyledButton>
                        <StyledButton type="primary" onClick={() => { setConfirmModalOpen(false); openEditModal(selectedRow!) }}>
                            Yes, I understand
                        </StyledButton>
                    </FooterButtons>
                }
            >
                <div style={{ color: "#ff4d4f", fontSize: "16px", marginBottom: "24px", paddingTop: "24px" }}>The Cut-off date is less than 60 hours away. Are you sure you want to proceed with adding pricing?</div>
            </StyledModal>}
        </>
    );
};

export default DimensionsTable;
